import { useState } from 'react';
import { motion } from 'framer-motion';
import { StarIcon, XIcon } from './icons';
import { apiClient } from '../services/api';
import type { FeedbackPayload, WidgetAuth } from '../types/api';

interface FeedbackFormProps {
  callId: number;
  onClose: () => void;
  onSubmit: () => void;
  /** Called on submit failure with error message; parent can wire to toast */
  onError?: (error: string) => void;
  auth?: WidgetAuth;
}

// Five stars, because that is the scale the rest of the product already speaks:
// the widget's own feedback form, and the Backoffice report that renders these
// same rows (its star strip is a fixed 1..5 and lights a star when its number is
// <= the stored value). A 10 written here came back as a flawless 5/5 there and
// silently inflated `average_stars` — the two forms post to the same /reviews
// endpoint, and Portal stores `stars` as a bare float with no upper bound.
const RATING_MAX = 5;
const RATING_SCALE = Array.from({ length: RATING_MAX }, (_, i) => i + 1);

const feedbackReasons = [
  'Product knowledge',
  'Communication skills',
  'Responsiveness',
  'Overall experience',
  'Other',
];

// The form asks for a rating, what stood out, and a comment — nothing else.
//
// It used to ask for an e-mail "for follow-up", pre-filled from the appointment,
// and for consent to use it. Both were dropped on 2026-09-17: the address is
// already on the appointment this review belongs to, so the field asked the
// customer to type back something we sent their invitation to, and nothing shows
// it — neither the Consumer reviews report in Backoffice nor the widget's own
// feedback form, which posts a rating and a comment and stops there.
export function FeedbackForm({ callId, onClose, onSubmit, onError, auth }: FeedbackFormProps) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [reason, setReason] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const combinedMessage = [reason, message].filter((s) => s && s.trim()).join(' — ').trim();
      const payload: FeedbackPayload = {
        call_id: callId,
        stars: rating,
        message: combinedMessage || undefined,
      };

      await apiClient.submitFeedback(payload, auth!);
      onSubmit();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit feedback';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl max-w-md w-full p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-serif text-brand-navy">Share Your Feedback</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <XIcon size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Rating */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              How would you rate your consultation?
            </label>
            <div className="flex justify-center gap-1">
              {RATING_SCALE.map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRating(value)}
                  onMouseEnter={() => setHoverRating(value)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1"
                >
                  <StarIcon
                    size={24}
                    className={`transition-colors ${
                      value <= (hoverRating || rating)
                        ? 'text-brand-gold fill-brand-gold'
                        : 'text-gray-300'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-center text-sm text-gray-500 mt-1">
              {rating > 0 ? `${rating}/${RATING_MAX}` : 'Click to rate'}
            </p>
          </div>

          {/* Reason */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              What stood out to you?
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold"
            >
              <option value="">Select a reason (optional)</option>
              {feedbackReasons.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          {/* Message */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Additional comments (optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-gold resize-none"
              placeholder="Tell us more about your experience..."
            />
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-500 text-sm mb-4">{error}</p>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={rating === 0 || isSubmitting}
            className="w-full py-3 bg-brand-gold text-white font-medium rounded-lg hover:bg-brand-gold-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}
